import { formatDate } from '../../../utils/format';
const toStatusNumber = value => {
  if (value === null || value === undefined || value === '') return null;
  const key = Number(value);
  return Number.isNaN(key) ? null : key;
};

const toLabel = value =>
  String(value ?? '')
    .trim()
    .toLowerCase();

export const CLARIFICATION_STATUS = {
  OPEN: 0,
  WAITING_DMUT: 1,
  WAITING_PANITIA: 2,
  CLOSING: 3,
};

export const CLARIFICATION_PROGRESS = {
  OPEN: 0,
  WAITING_DMUT: 1,
  WAITING_PANITIA: 2,
  CLOSING: 3,
};

const STATUS_FALLBACK_LABEL = {
  0: 'Open',
  1: 'Waiting Response from Registered Supplier',
  2: 'Waiting Response from Panitia Tender',
  3: 'Closing Clarification',
};

const PROGRESS_FALLBACK_LABEL = {
  0: 'Open',
  1: 'Waiting Response from Registered Supplier',
  2: 'Waiting Response from Panitia Tender',
  3: 'Closing',
};

export const CLARIFICATION_STATUS_BADGE_COLOR = {
  0: 'secondary',
  1: 'warning',
  2: 'warning',
  3: 'danger',
};

export const CLARIFICATION_PROGRESS_BADGE_COLOR = {
  0: 'primary',
  1: 'warning',
  2: 'warning',
  3: 'danger',
};

const resolveBadge = (value, apiLabel, fallback, colorMap, fallbackLabels) => {
  const key = toStatusNumber(value);
  const trimmedLabel =
    apiLabel === null || apiLabel === undefined ? '' : String(apiLabel).trim();
  const mapped =
    key === null ? fallbackLabels[0] : (fallbackLabels[key] ?? String(value));
  const label =
    key !== null && fallbackLabels[key]
      ? fallbackLabels[key]
      : trimmedLabel || mapped || fallback;

  if (value === null || value === undefined || value === '') {
    return {
      label: trimmedLabel || fallbackLabels[0] || fallback,
      color: colorMap[0],
    };
  }

  return {
    label,
    color: key === null ? 'secondary' : (colorMap[key] ?? 'secondary'),
  };
};

export const getClarificationStatusBadge = (
  value,
  apiLabel = null,
  fallback = '-'
) =>
  resolveBadge(
    value,
    apiLabel,
    fallback,
    CLARIFICATION_STATUS_BADGE_COLOR,
    STATUS_FALLBACK_LABEL
  );

export const getClarificationProgressBadge = (
  value,
  apiLabel = null,
  fallback = '-'
) =>
  resolveBadge(
    value,
    apiLabel,
    fallback,
    CLARIFICATION_PROGRESS_BADGE_COLOR,
    PROGRESS_FALLBACK_LABEL
  );

const isClosingLabel = value => {
  const label = toLabel(value);
  return (
    label === 'closing' ||
    label === 'closed' ||
    label === 'closing clarification'
  );
};

const isWaitingDmutLabel = value => {
  const label = toLabel(value);
  return (
    label === 'waiting response from registered supplier' ||
    label === 'waiting respond from registered supplier'
  );
};

const isWaitingPanitiaLabel = value => {
  const label = toLabel(value);
  return (
    label === 'waiting response from panitia tender' ||
    label === 'waiting respond from panitia tender'
  );
};

export const isClarificationProgressClosing = progress => {
  const key = toStatusNumber(progress);
  if (key === CLARIFICATION_PROGRESS.CLOSING) return true;
  return isClosingLabel(progress);
};

export const isClarificationProgressOpen = progress => {
  if (progress === null || progress === undefined || progress === '')
    return true;
  const key = toStatusNumber(progress);
  if (key === CLARIFICATION_PROGRESS.OPEN) return true;
  return toLabel(progress) === 'open';
};

export const canTeClarificationActions = status => {
  const key = toStatusNumber(status);
  if (key === null) return true;
  if (
    key === CLARIFICATION_STATUS.OPEN ||
    key === CLARIFICATION_STATUS.WAITING_PANITIA
  ) {
    return true;
  }
  const label = toLabel(status);
  return label === 'open' || isWaitingPanitiaLabel(status);
};

export const isTeClarificationEditable = status =>
  canTeClarificationActions(status);

export const isTeClarificationLocked = status => {
  const key = toStatusNumber(status);
  if (key === null) return false;
  if (
    key === CLARIFICATION_STATUS.WAITING_DMUT ||
    key === CLARIFICATION_STATUS.CLOSING
  ) {
    return true;
  }
  return isWaitingDmutLabel(status) || isClosingLabel(status);
};

export const isIcClarificationEditable = progress => {
  if (progress === null || progress === undefined || progress === '')
    return true;
  const key = toStatusNumber(progress);
  if (
    key === CLARIFICATION_PROGRESS.OPEN ||
    key === CLARIFICATION_PROGRESS.WAITING_DMUT
  ) {
    return true;
  }
  const label = toLabel(progress);
  return label === 'open' || isWaitingDmutLabel(progress);
};

export const isIcClarificationLocked = progress =>
  !isIcClarificationEditable(progress);

export const isClarificationStatusSubmitted = value => {
  const key = toStatusNumber(value);
  if (
    key === CLARIFICATION_STATUS.WAITING_DMUT ||
    key === CLARIFICATION_STATUS.CLOSING
  ) {
    return true;
  }
  return isWaitingDmutLabel(value) || isClosingLabel(value);
};

export const isClarificationClosedOrNoClarification = bidder => {
  if (!bidder) return false;

  const status = bidder.statusClarification;
  const statusLabel = String(bidder.statusClarificationLabel ?? status ?? '')
    .trim()
    .toLowerCase();
  const statusKey = toStatusNumber(status);

  if (
    statusKey === CLARIFICATION_STATUS.CLOSING ||
    isClosingLabel(statusLabel)
  ) {
    return true;
  }

  const progress = bidder.statusClarificationProgress;
  const progressLabel = String(
    bidder.statusClarificationProgressLabel ?? progress ?? ''
  )
    .trim()
    .toLowerCase();

  if (
    toStatusNumber(progress) === CLARIFICATION_PROGRESS.CLOSING ||
    isClosingLabel(progressLabel)
  ) {
    return true;
  }

  return false;
};

export const areAllBiddersClarificationDone = (items = []) => {
  if (!Array.isArray(items) || items.length === 0) return false;
  return items.every(isClarificationClosedOrNoClarification);
};

export const formatSubmittedDate = formatDate;
